import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Users, Calendar, User, Share, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Club {
  id: number;
  name: string;
  location: string;
  distance: string;
  rating: number;
  sports: string[];
  memberCount: number;
}

interface Event {
  id: number;
  title: string;
  organizer: string;
  sport: string;
  startDate: string;
  endDate: string;
  time: string;
  location: string;
  category: string;
  partner?: string;
  status: "upcoming" | "active" | "finished";
  registrationDeadline?: string;
}

interface SuggestedClubsSectionProps {
  clubs: Club[] | Event[];
  onExploreMore: () => void;
  title?: string;
  showAsEvents?: boolean;
  keyPrefix?: string; // Add keyPrefix to ensure unique keys
}

const SuggestedClubsSection = ({ 
  clubs, 
  onExploreMore, 
  title = "Clubes sugeridos cerca de ti",
  showAsEvents = false,
  keyPrefix = "item"
}: SuggestedClubsSectionProps) => {
  const navigate = useNavigate();

  const getSportColor = (sport: string) => {
    switch (sport.toLowerCase()) {
      case 'pádel':
        return 'bg-emerald-100 text-emerald-800';
      case 'tenis':
        return 'bg-green-100 text-green-800';
      case 'pickleball':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTournamentFormatDisplay = (tournamentFormat?: string) => {
    switch (tournamentFormat) {
      case 'round-robin':
        return 'Round Robin';
      case 'sumatoria-juegos':
        return 'Sumatoria de Juegos';
      case 'eliminacion-directa':
        return 'Eliminación Directa';
      default:
        return null;
    }
  };

  const getTournamentFormatColor = (tournamentFormat?: string) => {
    switch (tournamentFormat) {
      case 'round-robin':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'sumatoria-juegos':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'eliminacion-directa':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const formatEventDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleEventClick = (event: Event) => {
    // Solo navegar a detalles para torneos (no ligas)
    const isTournament = event.title.toLowerCase().includes('torneo');
    if (isTournament) {
      navigate(`/tournament/${event.id}`);
    }
  };

  const handleShare = (event: Event, e: React.MouseEvent) => {
    e.stopPropagation();
    const eventUrl = `${window.location.origin}/event/${event.id}`;
    const shareText = `¡Únete a ${event.title}! ${event.location} - ${formatEventDate(event.startDate)}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${eventUrl}`)}`;
    
    if (navigator.share) {
      navigator.share({
        title: event.title,
        text: shareText,
        url: eventUrl,
      }).catch(console.error);
    } else {
      // Fallback: open WhatsApp
      window.open(whatsappUrl, '_blank');
    }
  };

  const calculateDaysUntilDeadline = (deadline: string) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDeadlineDisplay = (registrationDeadline?: string) => {
    if (!registrationDeadline) return null;
    
    const daysUntil = calculateDaysUntilDeadline(registrationDeadline);
    
    if (daysUntil < 0) {
      return {
        text: "Inscripciones cerradas",
        color: "text-gray-500",
        bgColor: "bg-gray-100"
      };
    } else if (daysUntil === 0) {
      return {
        text: "¡Último día!",
        color: "text-red-700",
        bgColor: "bg-red-100"
      };
    } else if (daysUntil <= 3) {
      return {
        text: `${daysUntil} día${daysUntil > 1 ? 's' : ''} restante${daysUntil > 1 ? 's' : ''}`,
        color: "text-red-700",
        bgColor: "bg-red-100"
      };
    } else {
      return {
        text: `${daysUntil} días restantes`,
        color: "text-orange-700",
        bgColor: "bg-orange-100"
      };
    }
  };

  const renderEventCard = (event: Event, index: number) => {
    const tournamentFormatDisplay = getTournamentFormatDisplay((event as any).tournamentFormat);
    const isTournament = event.title.toLowerCase().includes('torneo');
    const deadlineDisplay = getDeadlineDisplay((event as any).registrationDeadline);
    
    return (
      <Card 
        key={`${keyPrefix}-event-${event.id}-${index}`}
        className={`bg-white/70 backdrop-blur-sm hover:shadow-md transition-shadow ${
          isTournament ? 'cursor-pointer' : ''
        }`}
        onClick={() => handleEventClick(event)}
      >
        <CardContent className="p-4">
          <div className="space-y-3">
            <div className="flex justify-between items-start">
              <h3 className="font-semibold text-gray-900 text-sm">{event.title}</h3>
              <div className="flex items-center gap-1">
                <Badge className={getSportColor(event.sport)}>
                  {event.sport}
                </Badge>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="p-1 h-6 w-6 hover:bg-gray-100"
                  onClick={(e) => handleShare(event, e)}
                >
                  <Share className="h-3 w-3 text-gray-600" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center text-xs text-gray-600">
                <Calendar className="h-3 w-3 mr-1" />
                <span>{formatEventDate(event.startDate)}</span>
              </div>
              
              <div className="flex items-center text-xs text-gray-600">
                <MapPin className="h-3 w-3 mr-1" />
                <span>{event.location}</span>
              </div>
              
              {event.partner && (
                <div className="flex items-center text-xs text-gray-600">
                  <User className="h-3 w-3 mr-1" />
                  <span>Compañero: {event.partner}</span>
                </div>
              )}
            </div>

            {/* Registration Deadline - Only for tournaments, positioned FIRST */}
            {isTournament && deadlineDisplay && (
              <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${deadlineDisplay.bgColor}`}>
                <Clock className="h-3 w-3" />
                <span className={`text-xs font-medium ${deadlineDisplay.color}`}>
                  {deadlineDisplay.text}
                </span>
              </div>
            )}

            {/* Tournament Format Badge - Only for tournaments, positioned AFTER deadline */}
            {isTournament && tournamentFormatDisplay && (
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={`text-xs ${getTournamentFormatColor((event as any).tournamentFormat)}`}>
                  {tournamentFormatDisplay}
                </Badge>
              </div>
            )}
            
            <div className="flex justify-between items-center pt-2">
              <Badge variant="outline" className="text-xs">
                Cat. {event.category}
              </Badge>
              <span className="text-xs text-gray-500">{event.organizer}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderClubCard = (club: Club, index: number) => (
    <Card key={`${keyPrefix}-club-${club.id}-${index}`} className="bg-white/70 backdrop-blur-sm">
      <CardContent className="p-4">
        <div className="space-y-2">
          <div className="flex justify-between items-start">
            <h3 className="font-semibold text-gray-900 text-sm">{club.name}</h3>
            <div className="flex items-center text-xs text-amber-600">
              <span>★ {club.rating}</span>
            </div>
          </div>
          
          <div className="flex items-center text-xs text-gray-600">
            <MapPin className="h-3 w-3 mr-1" />
            <span>{club.location}</span>
          </div>
          
          <div className="flex items-center text-xs text-gray-600">
            <Users className="h-3 w-3 mr-1" />
            <span>{club.memberCount} miembros</span>
          </div>
          
          <div className="flex flex-wrap gap-1 pt-1">
            {club.sports.slice(0, 2).map((sport) => (
              <Badge key={sport} variant="outline" className="text-xs">
                {sport}
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (clubs.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        <Button 
          variant="outline" 
          size="sm"
          onClick={onExploreMore}
          className="text-primary border-primary/20 hover:bg-primary/5"
        >
          Ver todos
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {clubs.slice(0, 6).map((item, index) => 
          showAsEvents ? renderEventCard(item as Event, index) : renderClubCard(item as Club, index)
        )}
      </div>
    </div>
  );
};

export default SuggestedClubsSection;
